/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { Competition, CompetitionInput } from '@/shared/models/competitions.interface';
import {
  GET_CLASSIFICATION_SUMMARY,
  GET_COMPETITION_INSCRIPTIONS,
  GET_COMPETITIONS,
} from '@/shared/services/queries/competitions.graphql';

import { Team, TeamWithKPIs } from '../models/team.model';
import { sortByCriteria } from '../utils/sort.utils';

@Injectable({
  providedIn: 'root',
})
export class CompetitionsService {
  private readonly apollo = inject(Apollo);

  getCompetitions(filter: CompetitionInput): Observable<any> {
    return this.apollo
      .watchQuery({
        query: GET_COMPETITIONS,
        variables: { filter },
      })
      .valueChanges.pipe(
        take(1),
        map((result: any) => sortByCriteria<Competition>(result.data.competitions, (item: Competition) => item.name))
      );
  }

  getCompetition(filter: CompetitionInput): Observable<Competition[]> {
    return this.apollo
      .watchQuery<{ competitions: Competition[] }>({
        query: GET_COMPETITIONS,
        variables: { filter },
      })
      .valueChanges.pipe(
        take(1),
        map((r) => {
          return r.data.competitions;
        })
      );
  }

  getCompetitionInscriptions(competitionId?: number | null, seasonId?: number, teamId?: number): Observable<any[]> {
    const filter: any = { competitionId };
    if (seasonId != null) filter.seasonId = seasonId;
    if (teamId != null) filter.teamId = teamId;
    return this.apollo
      .watchQuery({
        query: GET_COMPETITION_INSCRIPTIONS,
        variables: { filter },
      })
      .valueChanges.pipe(map((result: any) => result.data.competitionInscriptions));
  }

  getCompetitionClassifications(
    competitionId: number,
    seasonId: number,
    inscriptions: { teamId: number }[]
  ): Observable<TeamWithKPIs[]> {
    return this.apollo
      .watchQuery<any>({
        query: GET_CLASSIFICATION_SUMMARY,
        variables: { competitionId, seasonId },
      })
      .valueChanges.pipe(
        map((res) => {
          interface TeamKpi {
            value: number;
            teamId: number;
          }

          const expected: TeamKpi[] = res.data.expectedPointsByTeam || [];
          const xpts: TeamKpi[] = res.data.xPtsChangeByTeam || [];
          const allTeams: Team[] = res.data.teams || [];

          // Solo los equipos inscritos
          const registeredTeamIds = inscriptions.map((i) => i.teamId);
          const filteredTeams = allTeams.filter((t) => registeredTeamIds.includes(t.id));

          return filteredTeams.map((team: Team) => ({
            ...team,
            expectedPoints: expected.find((e) => e.teamId === team.id)?.value ?? 0,
            xPtsChange: xpts.find((x) => x.teamId === team.id)?.value ?? 0,
          }));
        })
      );
  }
}
